// ⚠️⚠️⚠️
// 大前提、js の Date 型自体は timezone を持たず、toString, getHours, setHours などをしたタイミングで timezone が考慮された値について
// 操作されるということを理解すること。
//
// ⚠️ そのため、どの timezone にいても日本時間の値を取得するようなことは非常に工夫が必要であり、注意する必要がある ⚠️
//
// 特に setXXX は非常に複雑な操作が行われるので、このファイル以外のアプリケーションコードでは使わないことが望ましい。
//
// また、どの timezone にいても日本の時刻を取得したい場合は、 Date + getXXX などを使用するのではなく、 src/utils/jst_date.ts の getJstXXX 関数を使用すること。
// ⚠️⚠️⚠️

import {
	getJstDate,
	getJstDay,
	getJstFullYear,
	getJstHours,
	getJstMinute,
	getJstMonth,
	getJstTwoDigitDateStr,
	getJstTwoDigitHourStr,
	getJstTwoDigitMinuteStr,
	getJstTwoDigitMonthStr,
	getJstTwoDigitSecondStr,
} from "./jst_date"

export const isValidDate = (date?: Date): boolean => {
	return !isNaN(date?.getTime() ?? NaN)
}

export const formatJstDateUntilDay = (date: Date): string => {
	const year = getJstFullYear(date)
	const month = getJstTwoDigitMonthStr(date)
	const day = getJstTwoDigitDateStr(date)

	return `${year}/${month}/${day}`
}

export const formatJstDateUntilDayByHyphen = (date: Date): string => {
	const year = getJstFullYear(date)
	const month = getJstTwoDigitMonthStr(date)
	const day = getJstTwoDigitDateStr(date)

	return `${year}-${month}-${day}`
}

export const formatJstHourMinuteOfDate = (date: Date): string => {
	const hour = getJstTwoDigitHourStr(date)
	const minute = getJstTwoDigitMinuteStr(date)
	return `${hour}:${minute}`
}

export const formatJstDateUntilMinute = (date: Date): string => {
	return `${formatJstDateUntilDayByHyphen(date)}(${getJstWeekDay(date)}) ${formatJstHourMinuteOfDate(date)}`
}

export const getJstWeekDay = (date: Date): string => {
	const weekDays = ["日", "月", "火", "水", "木", "金", "土"]
	return weekDays[getJstDay(date)]
}

export const convertDateToHumanReadableJstDateStr = (date: Date): string => {
	const year = getJstFullYear(date)
	const month = getJstTwoDigitMonthStr(date)
	const day = getJstTwoDigitDateStr(date)
	const hours = getJstTwoDigitHourStr(date)
	const minutes = getJstTwoDigitMinuteStr(date)

	return `${year}/${month}/${day} ${hours}:${minutes}`
}

export const addDate = (date: Date, toAdd: number): Date => {
	return new Date(date.getTime() + toAdd * 24 * 60 * 60 * 1000)
}

export const addMinute = (date: Date, toAdd: number): Date => {
	return new Date(date.getTime() + toAdd * 60 * 1000)
}

export const dayEquals = (a: Date, b: Date): boolean => {
	return (
		getJstFullYear(a) === getJstFullYear(b) &&
		getJstMonth(a) === getJstMonth(b) &&
		getJstDate(a) === getJstDate(b)
	)
}

// getJSTISOString: 2024-12-26T11:00:00+09:00
export const getJSTISOString = (date: Date) => {
	return `${getJstFullYear(date)}-${getJstTwoDigitMonthStr(date)}-${getJstTwoDigitDateStr(date)}T${getJstTwoDigitHourStr(date)}:${getJstTwoDigitMinuteStr(date)}:${getJstTwoDigitSecondStr(date)}+09:00`
}

/**
 * Date を HTML の datetime-local 入力用の形式に変換する
 * @param date - 変換元のDate
 * @returns datetime-local 形式の文字列 (yyyy-mm-ddThh:mm)
 * @example formatDateToDatetimeLocal(new Date("2024-12-26T11:00:00+09:00")) // "2024-12-26T11:00"
 */
export const formatDateToDatetimeLocal = (date: Date): string => {
	return `${formatJstDateUntilDayByHyphen(date)}T${formatJstHourMinuteOfDate(date)}`
}

/**
 * HTML の datetime-local 形式を JST の ISO 8601 形式に変換する
 * @param datetimeLocal - datetime-local 形式の文字列 (yyyy-mm-ddThh:mm)
 * @returns JST の ISO 8601 形式の文字列 (yyyy-mm-ddThh:mm:ss+09:00)
 * @example formatDatetimeLocalToJstIso("2024-12-26T11:00") // "2024-12-26T11:00:00+09:00"
 */
export const formatDatetimeLocalToJstIso = (datetimeLocal: string): string => {
	return `${datetimeLocal}:00+09:00`
}

export const newDateByJstParams = (
	year: number,
	month: number, // 0-11
	day: number,
	hour: number,
	minute: number
): Date => {
	return new Date(
		`${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00+09:00`
	)
}

// date: yyyy-mm-dd
// TODO: graphql の Date 型が yyyy-mm-dd のときと yyyy-mm-ddThh:mm:ss のときがあり、わかりずらいのでなんとかする
export const formatGraphQLDayToJapaneseMonthDay = (date: string): string => {
	const [year, month, day] = date.split("-")
	if (!year || !month || !day) {
		throw new Error("Invalid date format")
	}
	const m = String(Number(month)) // truncate 0 prefix
	const d = String(Number(day)) // truncate 0 prefix

	const dateObj = newDateByGraphQLDayAndHourAndMinute(date, 0, 0)
	const weekDay = getJstWeekDay(dateObj)

	return `${m}月${d}日（${weekDay}）`
}

// dateStr: yyyy-mm-dd
// TODO: graphql の Date 型が yyyy-mm-dd のときと yyyy-mm-ddThh:mm:ss のときがあり、わかりずらいのでなんとかする
export const newDateByGraphQLDayAndHourAndMinute = (
	dateStr: string,
	hour: number,
	minute: number
): Date => {
	return new Date(
		dateStr +
			"T" +
			hour.toString().padStart(2, "0") +
			":" +
			minute.toString().padStart(2, "0") +
			":00+09:00"
	)
}

// dateStr: yyyy-mm-ddThh:mm:ss
// TODO: graphql の Date 型が yyyy-mm-dd のときと yyyy-mm-ddThh:mm:ss のときがあり、わかりずらいのでなんとかする
export const convertGraphQLDateToDate = (dateStr: string): Date => {
	const untilDay = dateStr.split("T")[0]
	const HourMinuteSecond = dateStr.split("T")[1]

	const HourMinuteSecondSplited = HourMinuteSecond.split(":")
	const hour = Number(HourMinuteSecondSplited[0])
	const minute = Number(HourMinuteSecondSplited[1])
	return newDateByGraphQLDayAndHourAndMinute(untilDay, hour, minute)
}

/** ISO 日付文字列を "YYYY/MM/DD" にフォーマットする */
export const formatIsoDate = (dateStr: string): string => {
	const d = new Date(dateStr)
	const y = getJstFullYear(d)
	const m = String(getJstMonth(d) + 1).padStart(2, "0")
	const day = String(getJstDate(d)).padStart(2, "0")
	return `${y}/${m}/${day}`
}

/** ISO 日時文字列を "YYYY/MM/DD HH:mm" にフォーマットする */
export const formatIsoDateTime = (dateStr: string): string => {
	const d = new Date(dateStr)
	const y = getJstFullYear(d)
	const mo = String(getJstMonth(d) + 1).padStart(2, "0")
	const day = String(getJstDate(d)).padStart(2, "0")
	const h = String(getJstHours(d)).padStart(2, "0")
	const mi = String(getJstMinute(d)).padStart(2, "0")
	return `${y}/${mo}/${day} ${h}:${mi}`
}

/** 年月オブジェクトを "YYYY年M月" にフォーマットする */
export const formatYearMonth = (ym: {
	year: number
	month: number
}): string => {
	return `${ym.year}年${ym.month}月`
}
